import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Button, Card, message, Typography, Row, Col, Select, Checkbox,
    DatePicker, Form, Divider, Space, Modal, Input, Alert, Table
} from "antd";
import {
    DownloadOutlined, SaveOutlined, DeleteOutlined,
    FileExcelOutlined, FilePdfOutlined, FileTextOutlined,
    ClockCircleOutlined, InfoCircleOutlined, UndoOutlined
} from "@ant-design/icons";
import ExportServ from "../../service/ExportServ";
import Breadcrumbs from "../../component/Breadcrumbs";
import Notify from "../../util/Notify";
import ShopServ from "../../service/ShopServ";
import moment from 'moment';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const SELLER_ENTITIES = [
    { label: 'Products', value: 'Product', fields: ['name', 'description', 'hsnCode', 'mrp', 'sellerPrice', 'discount', 'stock', 'lowStockThreshold', 'imageUrl', 'images', 'gallerySource', 'updatedAt', 'categoryId', 'subCategoryId', 'brandId', 'shopId'], dateFields: ['createdAt', 'updatedAt'] },
    { label: 'Product Options (Detailed)', value: 'ProductOption', fields: ['productId', 'productName', 'optionGroup', 'variantValue', 'variantPrice', 'variantMrp', 'variantStock', 'variantSku', 'variantImage'], dateFields: ['createdAt'] },
    { label: 'Orders', value: 'Order', fields: ['orderId', 'totalAmount', 'payableAmount', 'deliveryCharge', 'extraDeliveryCharge', 'handlingFee', 'walletUsedAmount', 'codAmount', 'couponDiscount', 'status', 'paymentStatus', 'paymentMethod', 'paymentCollectionMethod', 'deliveryPaymentStatus', 'returnStatus', 'sellerPayoutStatus', 'sellerFinalEarnings', 'adminCommissionAmount', 'couponCode', 'estimatedDeliveryTime', 'deliveredAt', 'createdAt', 'updatedAt'], dateFields: ['createdAt', 'updatedAt', 'scheduledDate', 'deliveredAt'] },
    { label: 'Sales Report', value: 'ProductSold', fields: ['productId', 'productName', 'quantity', 'priceAtSale', 'totalPrice', 'customerName', 'customerPhone', 'notes', 'soldAt', 'createdAt', 'updatedAt'], dateFields: ['createdAt', 'soldAt'] }
];

const ADMIN_ENTITIES = [
    { label: 'Products', value: 'Product', fields: ['name', 'description', 'hsnCode', 'price', 'mrp', 'sellerPrice', 'discount', 'adminMarkupType', 'adminMarkupValue', 'categoryCommissionPercentage', 'stock', 'lowStockThreshold', 'imageUrl', 'images', 'gallerySource', 'hasVariants', 'isLive', 'approvalStatus', 'updatedAt', 'categoryId', 'subCategoryId', 'brandId', 'shopId', 'options'], dateFields: ['createdAt', 'updatedAt'] },
    { label: 'Product Options (Detailed)', value: 'ProductOption', fields: ['productId', 'productName', 'optionGroup', 'variantValue', 'variantPrice', 'variantMrp', 'variantStock', 'variantSku', 'variantImage'], dateFields: ['createdAt'] },
    { label: 'Orders', value: 'Order', fields: ['orderId', 'totalAmount', 'payableAmount', 'deliveryCharge', 'extraDeliveryCharge', 'handlingFee', 'walletUsedAmount', 'codAmount', 'couponDiscount', 'status', 'paymentStatus', 'paymentMethod', 'paymentCollectionMethod', 'deliveryPaymentStatus', 'returnStatus', 'sellerPayoutStatus', 'sellerFinalEarnings', 'adminCommissionAmount', 'couponCode', 'estimatedDeliveryTime', 'deliveredAt', 'createdAt', 'updatedAt'], dateFields: ['createdAt', 'updatedAt', 'scheduledDate', 'deliveredAt'] },
    { label: 'Offers/Coupons', value: 'Coupon', fields: ['code', 'discountType', 'discountValue', 'minOrderAmount', 'maxDiscountAmount', 'startDate', 'endDate', 'usageLimit', 'usedCount', 'isActive', 'isTargeted', 'isLive', 'createdAt', 'updatedAt'], dateFields: ['createdAt', 'startDate', 'endDate'] },
    { label: 'Sales Report', value: 'ProductSold', fields: ['productId', 'productName', 'quantity', 'priceAtSale', 'totalPrice', 'customerName', 'customerPhone', 'notes', 'soldAt', 'createdAt', 'updatedAt'], dateFields: ['createdAt', 'soldAt'] },
    { label: 'Users', value: 'User', fields: ['userId', 'firstName', 'lastName', 'email', 'phone', 'profilePhoto', 'role', 'isActive', 'isBlocked', 'walletBalance', 'credits', 'messagesCount', 'unreadNotifications', 'bankDetails.bankName', 'bankDetails.accountHolderName', 'bankDetails.accountNumber', 'bankDetails.ifscCode', 'termsAccepted', 'createdAt', 'updatedAt'], dateFields: ['createdAt', 'updatedAt'] },
    { label: 'Shops', value: 'Shop', fields: ['shopId', 'name', 'description', 'imageUrl', 'isActive', 'isTop', 'rating', 'ratingCount', 'approvalStatus', 'createdAt', 'updatedAt'], dateFields: ['createdAt', 'updatedAt'] },
    { label: 'Categories (Parents)', value: 'Category', fields: ['name', 'description', 'imageUrl', 'isActive', 'isLive', 'priceHandling.type', 'priceHandling.value', 'defaultCommissionPercentage', 'allowReturn', 'createdAt', 'updatedAt'], dateFields: ['createdAt', 'updatedAt'] },
    { label: 'Sub Categories', value: 'SubCategory', fields: ['name', 'parentId', 'description', 'imageUrl', 'isActive', 'isLive', 'priceHandling.type', 'priceHandling.value', 'defaultCommissionPercentage', 'allowReturn', 'createdAt', 'updatedAt'], dateFields: ['createdAt', 'updatedAt'] },
    { label: 'Seller Requests', value: 'SellerRequest', fields: ['name', 'email', 'phone', 'shopName', 'status', 'createdAt', 'updatedAt'], dateFields: ['createdAt', 'updatedAt'] },
    { label: 'Brands', value: 'Brand', fields: ['name', 'description', 'imageUrl', 'isActive', 'createdAt', 'updatedAt'], dateFields: ['createdAt', 'updatedAt'] },
    { label: 'Transactions', value: 'Transaction', fields: ['user', 'amount', 'currency', 'status', 'method', 'paymentSubMethod', 'bank', 'wallet', 'vpa', 'razorpayOrderId', 'razorpayPaymentId', 'description', 'createdAt', 'updatedAt'], dateFields: ['createdAt', 'updatedAt'] }
];

const FORMATS = [
    { label: 'Excel Spreadsheet (.xlsx)', value: 'excel', icon: <FileExcelOutlined /> },
    { label: 'CSV File (.csv)', value: 'csv', icon: <FileTextOutlined /> },
    { label: 'PDF Document (.pdf)', value: 'pdf', icon: <FilePdfOutlined /> }
];

const FieldSelectionTable = ({ value = [], onChange, availableFields = [] }) => {
    const dataSource = availableFields.map(field => ({
        key: field,
        fieldName: field === 'imageUrl' ? 'Main Image' :
                    field === 'images' ? 'Gallery' :
                    field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1'),
        fieldId: field
    }));

    const selectedDataSource = dataSource.filter(item => value.includes(item.key));

    const leftColumns = [
        {
            title: <span style={{ fontWeight: 600 }}>Available Fields</span>,
            dataIndex: 'fieldName',
            key: 'fieldName',
        }
    ];

    const rightColumns = [
        {
            title: <span style={{ fontWeight: 600 }}>Selected Fields (Internal ID)</span>,
            dataIndex: 'fieldId',
            key: 'fieldId',
            render: (text) => <Text type="secondary" style={{ fontSize: '12px' }}>{text}</Text>
        }
    ];

    return (
        <Row gutter={24}>
            <Col span={12}>
                <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <Table
                        dataSource={dataSource}
                        columns={leftColumns}
                        pagination={false}
                        rowSelection={{
                            selectedRowKeys: value,
                            onChange: (selectedRowKeys) => {
                                onChange?.(selectedRowKeys);
                            }
                        }}
                        size="small"
                        scroll={{ y: 250 }}
                    />
                </div>
            </Col>
            <Col span={12}>
                <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', height: '100%' }}>
                    {selectedDataSource.length > 0 ? (
                        <Table
                            dataSource={selectedDataSource}
                            columns={rightColumns}
                            pagination={false}
                            size="small"
                            scroll={{ y: 250 }}
                        />
                    ) : (
                        <div style={{ height: '100%', minHeight: '290px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', padding: '40px', background: '#f8fafc' }}>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '32px', marginBottom: '8px' }}>📝</div>
                                <div style={{ fontWeight: 500 }}>No fields selected</div>
                                <div style={{ fontSize: '12px', marginTop: '4px' }}>Select fields from the left to include them in your export.</div>
                            </div>
                        </div>
                    )}
                </div>
            </Col>
        </Row>
    );
};

const ExportData = () => {
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [selectedEntity, setSelectedEntity] = useState(null);
    const [availableFields, setAvailableFields] = useState([]);
    const [dateFields, setDateFields] = useState(['createdAt']);
    const [isSaveModalVisible, setIsSaveModalVisible] = useState(false);
    const [newTemplateName, setNewTemplateName] = useState("");
    const [isAuthorized, setIsAuthorized] = useState(true);
    const [authChecking, setAuthChecking] = useState(true);
    const [shopsList, setShopsList] = useState([]);

    const role = localStorage.getItem("role");
    const ENTITIES = (role === "admin" || role === "superadmin") ? ADMIN_ENTITIES : SELLER_ENTITIES;

    useEffect(() => {
        checkShopAuth();
        fetchTemplates();
        fetchShops();
    }, []);

    const fetchShops = async () => {
        try {
            const currentRole = localStorage.getItem("role");
            let res;
            if (currentRole === 'admin' || currentRole === 'superadmin') {
                res = await ShopServ.adminIndex();
            } else {
                res = await ShopServ.getSellerShops();
            }
            const shopsData = res?.data || [];
            // For sellers, show active shops. For admins, show active/approved shops.
            const filteredShops = currentRole === 'seller'
                ? shopsData.filter(s => s.isActive)
                : shopsData.filter(s => s.isActive && s.approvalStatus === 'approved');
            setShopsList(filteredShops);
        } catch (error) {
            console.error("Failed to fetch shops for export filter", error);
        }
    };

    const checkShopAuth = async () => {
        const currentRole = localStorage.getItem("role");
        if (currentRole !== 'seller') {
            setIsAuthorized(true);
            setAuthChecking(false);
            return;
        }

        try {
            const shopsRes = await ShopServ.getSellerShops();
            const shops = shopsRes?.data || [];
            const activeShops = shops.filter(s => s.isActive);
            setIsAuthorized(activeShops.length > 0);
        } catch (error) {
            console.error("Auth check failed", error);
            setIsAuthorized(false);
        } finally {
            setAuthChecking(false);
        }
    };

    const fetchTemplates = async () => {
        try {
            const res = await ExportServ.getTemplates();
            setTemplates(res || []);
        } catch (error) {
            console.error("Failed to fetch templates", error);
        }
    };

    const handleEntityChange = (value) => {
        const entity = ENTITIES.find(e => e.value === value);
        setSelectedEntity(value);
        setAvailableFields(entity ? entity.fields : []);
        setDateFields(entity ? entity.dateFields : ['createdAt']);
        form.setFieldsValue({
            fields: entity ? entity.fields : [],
            dateFilterField: 'createdAt'
        });
    };

    const handleTemplateChange = (templateId) => {
        if (!templateId) {
            form.resetFields();
            setSelectedEntity(null);
            return;
        }

        const template = templates.find(t => t._id === templateId);
        if (template) {
            setSelectedEntity(template.entity);
            const entityData = ENTITIES.find(e => e.value === template.entity);
            setAvailableFields(entityData ? entityData.fields : []);
            setDateFields(entityData ? entityData.dateFields : ['createdAt']);

            form.setFieldsValue({
                entity: template.entity,
                fields: template.fields,
                dateFilterField: template.filters?.dateField || 'createdAt',
                shopId: template.filters?.shopId
            });
        }
    };

    const handleExport = async (values) => {
        try {
            setLoading(true);
            const { entity, fields, format, dateRange, dateFilterField, shopId } = values;

            const payload = {
                entity,
                fields,
                format,
                filters: {}
            };

            if (shopId && ['Product', 'ProductOption', 'Order', 'ProductSold', 'Shop'].includes(entity)) {
                payload.filters.shopId = shopId;
            }

            if (dateRange && dateRange.length === 2) {
                payload.dateRange = {
                    start: dateRange[0].startOf('day').toISOString(),
                    end: dateRange[1].endOf('day').toISOString(),
                    field: dateFilterField || 'createdAt'
                };
            }

            // NOTE: base.js interceptor returns response.data directly (the blob buffer).
            // For blobs, the interceptor already parses JSON errors embedded in blobs.
            const response = await ExportServ.exportData(payload);

            // Determine the correct MIME type and extension
            const extMap = { excel: 'xlsx', csv: 'csv', pdf: 'pdf' };
            const ext = extMap[format] || format;
            const mimeMap = {
                excel: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                csv: 'text/csv',
                pdf: 'application/pdf'
            };
            const mime = mimeMap[format] || 'application/octet-stream';

            // Wrap response in a Blob with correct MIME type for reliable downloads
            const blob = new Blob([response], { type: mime });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${entity}_Export_${moment().format('YYYYMMDD_HHmm')}.${ext}`);
            document.body.appendChild(link);
            link.click();

            // Cleanup
            setTimeout(() => {
                window.URL.revokeObjectURL(url);
                document.body.removeChild(link);
            }, 100);

            Notify.success("Export generated successfully!");
        } catch (error) {
            console.error("Export Error:", error);
            // error may be a string, Error object, or our custom error object from base.js
            const msg = (typeof error === 'string') ? error : (error?.message || "Export failed. Please try again.");
            Notify.error(msg);
        } finally {
            setLoading(false);
        }
    };

    const showSaveModal = () => {
        const values = form.getFieldsValue();
        if (!values.entity || !values.fields || values.fields.length === 0) {
            return message.warning("Please select a data collection and fields first!");
        }
        setIsSaveModalVisible(true);
    };

    const handleSaveTemplate = async () => {
        if (!newTemplateName) return message.error("Please enter a template name");

        try {
            const values = form.getFieldsValue();
            const showShopFilter = values.entity && ['Product', 'ProductOption', 'Order', 'ProductSold', 'Shop'].includes(values.entity);
            await ExportServ.createTemplate({
                name: newTemplateName,
                entity: values.entity,
                fields: values.fields,
                filters: {
                    dateField: values.dateFilterField || 'createdAt',
                    ...(showShopFilter && values.shopId ? { shopId: values.shopId } : {})
                }
            });
            message.success("Template saved successfully!");
            setIsSaveModalVisible(false);
            setNewTemplateName("");
            fetchTemplates();
        } catch (error) {
            message.error(error.message || "Failed to save template");
        }
    };

    const handleDeleteTemplate = async (e, id) => {
        e.stopPropagation();
        try {
            await ExportServ.deleteTemplate(id);
            message.success("Template removed");
            fetchTemplates();
        } catch (error) {
            message.error("Failed to remove template");
        }
    };

    const showShopFilter = selectedEntity && ['Product', 'ProductOption', 'Order', 'ProductSold', 'Shop'].includes(selectedEntity);
    const shopPlaceholder = (role === 'admin' || role === 'superadmin') ? "All Shops" : "All My Shops";

    return (
        <div className="pcu-padding-24">
            <div className="pcu-margin-bottom-16">
                <Breadcrumbs items={[{ label: 'Data Management' }, { label: 'Export Data Center' }]} />
            </div>

            <div className="main-content-card pcu-bg-white pcu-radius-20 pcu-shadow-sm pcu-overflow-hidden" style={{ border: 'none', padding: '32px' }}>
                {/* Dashboard Style Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px' }}>
                    <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                        <div style={{ 
                            width: '56px', 
                            height: '56px', 
                            background: '#fff7ed', 
                            borderRadius: '16px', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center' 
                        }}>
                            <DownloadOutlined style={{ fontSize: '26px', color: '#f97316' }} />
                        </div>
                        <div>
                            <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 800, color: '#1e293b' }}>Export Data Center</h1>
                            <Text type="secondary" style={{ fontSize: '13px' }}>Generate and archive your business intelligence reports</Text>
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <Button icon={<UndoOutlined />}>Reset All</Button>
                    </div>
                </div>

                {!isAuthorized && !authChecking && (
                    <Alert
                        message={<span style={{ fontWeight: 700 }}>Export Restricted</span>}
                        description="Your shop is not yet authorized. Data export features are disabled until your shop is approved by the administrator."
                        type="error"
                        showIcon
                        className="pcu-margin-bottom-32 pcu-radius-16"
                        action={
                            <Button size="large" danger ghost onClick={() => navigate('/contact')} style={{ borderRadius: '8px', fontWeight: 600 }}>
                                Contact Admin
                            </Button>
                        }
                    />
                )}

                <Row gutter={48} className={isAuthorized ? '' : 'pcu-opacity-60 pcu-pointer-none'}>
                    <Col lg={16} md={24}>
                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={handleExport}
                            initialValues={{ format: 'excel', dateFilterField: 'createdAt' }}
                            className="ss-form"
                        >
                            {/* Step 1: Collection & Template */}
                            <div style={{ marginBottom: 40 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
                                    <div style={{ 
                                        background: '#FF8110', 
                                        color: '#fff', 
                                        width: '36px', 
                                        height: '36px', 
                                        borderRadius: '10px', 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'center', 
                                        fontWeight: 800,
                                        fontSize: '16px',
                                        boxShadow: '0 4px 12px rgba(255, 129, 16, 0.2)'
                                    }}>1</div>
                                    <Title level={4} style={{ margin: 0, fontWeight: 700 }}>Select Data Source</Title>
                                </div>
                                <Row gutter={24}>
                                    <Col span={12}>
                                        <Form.Item label={<span style={{ fontWeight: 600, fontSize: '13px' }}>DATA COLLECTION</span>} name="entity" rules={[{ required: true }]}>
                                            <Select size="large" placeholder="Select collection" onChange={handleEntityChange} className="pcu-radius-12 ss-select">
                                                {ENTITIES.map(e => <Option key={e.value} value={e.value}>{e.label}</Option>)}
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item label={<span style={{ fontWeight: 600, fontSize: '13px' }}>SAVED TEMPLATE</span>} name="templateId">
                                            <Select size="large" placeholder="Reuse existing settings" onChange={handleTemplateChange} allowClear className="pcu-radius-12 ss-select">
                                                {templates.map(t => (
                                                    <Option key={t._id} value={t._id}>
                                                        <div className="pcu-flex pcu-justify-between pcu-items-center">
                                                            <span style={{ fontWeight: 600 }}>{t.name}</span>
                                                            <DeleteOutlined style={{ color: '#ef4444' }} onClick={(e) => handleDeleteTemplate(e, t._id)} />
                                                        </div>
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </div>

                            {/* Step 2: Fields */}
                            {selectedEntity && (
                                <div style={{ marginBottom: 40, padding: 32, background: '#f8fafc', borderRadius: '24px', border: '1px solid #f1f5f9' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
                                        <div style={{ 
                                            background: '#4f46e5', 
                                            color: '#fff', 
                                            width: '36px', 
                                            height: '36px', 
                                            borderRadius: '10px', 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center', 
                                            fontWeight: 800,
                                            fontSize: '16px',
                                            boxShadow: '0 4px 12px rgba(79, 70, 229, 0.2)'
                                        }}>2</div>
                                        <Title level={4} style={{ margin: 0, fontWeight: 700 }}>Choose Fields to Export</Title>
                                    </div>
                                    <Form.Item name="fields" rules={[{ required: true, message: 'Select at least one field' }]}>
                                        <FieldSelectionTable availableFields={availableFields} />
                                    </Form.Item>
                                </div>
                            )}

                            {/* Step 3: Filters & Format */}
                            <div style={{ marginBottom: 40 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
                                    <div style={{ 
                                        background: '#10b981', 
                                        color: '#fff', 
                                        width: '36px', 
                                        height: '36px', 
                                        borderRadius: '10px', 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'center', 
                                        fontWeight: 800,
                                        fontSize: '16px',
                                        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.2)'
                                    }}>3</div>
                                    <Title level={4} style={{ margin: 0, fontWeight: 700 }}>Format & Filters</Title>
                                </div>
                                <Row gutter={20}>
                                    {showShopFilter && (
                                        <Col span={8}>
                                            <Form.Item label={<span style={{ fontWeight: 600, fontSize: '12px' }}>SHOP FILTER</span>} name="shopId">
                                                <Select popupMatchSelectWidth={false} size="large" placeholder={shopPlaceholder} allowClear className="pcu-radius-10 ss-select">
                                                    {shopsList.map(shop => (
                                                        <Option key={shop._id} value={shop._id}>{shop.name}</Option>
                                                    ))}
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                    )}
                                    <Col span={showShopFilter ? 4 : 8}>
                                        <Form.Item label={<span style={{ fontWeight: 600, fontSize: '12px' }}>DATE FIELD</span>} name="dateFilterField">
                                            <Select size="large" className="pcu-radius-10 ss-select">
                                                {dateFields.map(df => (
                                                    <Option key={df} value={df}>{df.charAt(0).toUpperCase() + df.slice(1).replace(/([A-Z])/g, ' $1')}</Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                    <Col span={showShopFilter ? 6 : 8}>
                                        <Form.Item label={<span style={{ fontWeight: 600, fontSize: '12px' }}>DATE RANGE</span>} name="dateRange">
                                            <RangePicker size="large" className="pcu-w-100 pcu-radius-10" />
                                        </Form.Item>
                                    </Col>
                                    <Col span={showShopFilter ? 6 : 8}>
                                        <Form.Item label={<span style={{ fontWeight: 600, fontSize: '12px' }}>EXPORT FORMAT</span>} name="format">
                                            <Select size="large" className="pcu-radius-10 ss-select">
                                                {FORMATS.map(f => (
                                                    <Option key={f.value} value={f.value}>
                                                        <Space>{f.icon} {f.label.split(' (')[0]}</Space>
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </div>

                            <div className="pcu-flex pcu-gap-16">
                                <Button
                                    type="primary"
                                    size="large"
                                    icon={<DownloadOutlined />}
                                    htmlType="submit"
                                    loading={loading}
                                    style={{ 
                                        height: 56, 
                                        flex: 2, 
                                        background: '#FF8110', 
                                        borderColor: '#FF8110', 
                                        borderRadius: 14, 
                                        fontWeight: 700,
                                        fontSize: '16px',
                                        boxShadow: '0 8px 20px rgba(255, 129, 16, 0.15)'
                                    }}
                                >
                                    Generate & Download Report
                                </Button>
                                <Button
                                    size="large"
                                    icon={<SaveOutlined />}
                                    onClick={showSaveModal}
                                    style={{ height: 56, flex: 1, borderRadius: 14, fontWeight: 600, fontSize: '15px' }}
                                >
                                    Save Template
                                </Button>
                            </div>
                        </Form>
                    </Col>

                    <Col lg={8} md={24}>
                        <div style={{ padding: '32px', background: '#f8fafc', borderRadius: '24px', border: '1px solid #f1f5f9', marginBottom: '32px' }}>
                            <Title level={5} style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#1e293b' }}>
                                <InfoCircleOutlined style={{ color: '#4f46e5', fontSize: '20px' }} /> Pro Insights
                            </Title>
                            <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                {[
                                    { t: 'Excel (.xlsx)', d: 'Best for advanced data analysis and mathematical modeling.', c: '#4f46e5' },
                                    { t: 'CSV (.csv)', d: 'Optimal for importing data into third-party CRM and ERP systems.', c: '#10b981' },
                                    { t: 'PDF (.pdf)', d: 'Ideal for sharing static reports and document archives.', c: '#ef4444' }
                                ].map((tip, i) => (
                                    <div key={i} style={{ padding: '12px', background: '#fff', borderRadius: '12px', border: '1px solid #f1f5f9' }}>
                                        <div style={{ fontWeight: 700, fontSize: '13px', color: tip.c, marginBottom: '2px' }}>{tip.t}</div>
                                        <div style={{ fontSize: '11px', color: '#64748b', lineHeight: '1.4' }}>{tip.d}</div>
                                    </div>
                                ))}
                            </div>
                            <Divider style={{ margin: '24px 0' }} />
                            <div style={{ background: '#eef2ff', padding: '12px', borderRadius: '10px', display: 'flex', gap: '8px' }}>
                                <ClockCircleOutlined style={{ color: '#4f46e5', marginTop: '3px' }} />
                                <Text type="secondary" style={{ fontSize: '11px', color: '#4f46e5', fontWeight: 500 }}>
                                    Generating large reports may take a moment. Please keep this tab active until the download starts.
                                </Text>
                            </div>
                        </div>

                        <div style={{ padding: '32px', background: 'linear-gradient(135deg, #fdfcfb 0%, #fef9c3 100%)', borderRadius: '24px', border: '1px solid #fef08a' }}>
                            <Title level={5} style={{ margin: 0, fontSize: '15px', color: '#854d0e', fontWeight: 800 }}>BULK DATA INJECTION</Title>
                            <Text style={{ fontSize: '12px', color: '#a16207', display: 'block', marginTop: '12px', lineHeight: '1.5' }}>
                                Need to perform batch updates or upload new collections? Use our <strong>Universal Data Importer</strong> for high-speed synchronization.
                            </Text>
                            <Button 
                                type="link" 
                                style={{ padding: 0, color: '#FF8110', marginTop: '20px', fontWeight: 700, fontSize: '13px' }} 
                                onClick={() => navigate('/seller/import')}
                            >
                                Go to Importer →
                            </Button>
                        </div>
                    </Col>
                </Row>

                <Modal
                    title={<Title level={4} style={{ margin: 0, fontWeight: 800 }}>Save Export Template</Title>}
                    open={isSaveModalVisible}
                    onOk={handleSaveTemplate}
                    onCancel={() => setIsSaveModalVisible(false)}
                    okText="Save Template"
                    okButtonProps={{ style: { background: '#FF8110', borderColor: '#FF8110', height: '40px', borderRadius: '8px', fontWeight: 600 } }}
                    cancelButtonProps={{ style: { height: '40px', borderRadius: '8px' } }}
                    centered
                    width={450}
                    styles={{ body: { paddingTop: '10px' } }}
                >
                    <div style={{ marginBottom: 24 }}>
                        <Text type="secondary" style={{ fontSize: '13px' }}>Preserve your current field configuration and filters to reuse them for future reports.</Text>
                    </div>
                    <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', display: 'block', marginBottom: '8px', textTransform: 'uppercase' }}>TEMPLATE NAME</label>
                    <Input
                        size="large"
                        placeholder="e.g., Q4 Product Inventory"
                        value={newTemplateName}
                        onChange={(e) => setNewTemplateName(e.target.value)}
                        className="pcu-radius-10"
                    />
                </Modal>
            </div>
        </div>
    );
};

export default ExportData;
